<script setup>
import { useUsersStore, useMediaStore } from '@/stores'
import { mediaUrl } from '@core/utils/mediaUrl'

const props = defineProps({
  title: {
    type: String
  },
  preselectedMediaIds: {
    type: Array,
    default: () => []
  },
  isDialogVisible: {
    type: Boolean,
    required: true
  },
  crop: {
    type: Boolean,
    default: true
  },
  cropStencil: {
    type: String,
    default: "circle"
  },
  cropAspectRatio: {
    type: Number,
    default: 1
  },
  multiSelect: {
    type: Boolean,
    default: false
  },
  restaurant: {
    type: Object,
    required: false
  },
  userGroup: {
    type: Object,
    required: false
  },
})

// mediaStore.getMedia()

const emit = defineEmits([
  'media-selected',
])
const usersStore = useUsersStore()
let isEditingMyUser  = null
let myUser = null;
({ myUser, isEditingMyUser } = storeToRefs(usersStore))

const mediaStore = useMediaStore()
let media  = null;
({ media } = storeToRefs(mediaStore))
const setIsVisible = val => {
  emit('update:isDialogVisible', val)
}

const selectedIds = ref(props.preselectedMediaIds)
const selectedIdsOriginal = ref(Array(props.preselectedMediaIds))
const selectionOrder = ref([])

const selectedFile = ref(null)
const imagePreview = ref(null)

const inputRef = ref()
const isConfirmDialogOpen = ref(false)
const changesMade = ref(false)

const showMediaCropper = ref(false)
const mediaIdToCrop = ref(null)

const requestClose = () => {
  setIsVisible(false)
}

// Modify the selectMedia function for delete mode
const selectMedia = item => {
  if (deleteMode.value) {
    if (!isDeletable(item)) {
      alert("You can only delete your own images.")  // Or handle this message differently
      
      return
    }
    if(wasOriginallySelected(item)){
      alert("This image was being used, please save your settings and come back in here to delete later")  // Or handle this message differently
      
      return
    }
    const index = deleteSelection.value.indexOf(item.imageId)
    if (index === -1) {
      deleteSelection.value.push(item.imageId)
    } else {
      deleteSelection.value.splice(index, 1)
    }
  } else if (props.multiSelect) {
    const index = selectedIds.value.indexOf(item.imageId)
    if (index === -1) {
      selectedIds.value.push(item.imageId)
    } else {
      selectedIds.value.splice(index, 1)
    }
  } else {
    selectedIds.value = [item.imageId]
  }
  updateSelectionOrder()
}
const isDeletable = item => {
  return item.userId === myUser.value._id  // Assumes item has a userId field
}

const wasOriginallySelected = item => {
  return selectedIdsOriginal.value.includes(item.imageId)
}

const deleteMode = ref(false)
const deleteSelection = ref([])

const deleteConfirmed = async imageId => {
  await usersStore.showLoadingOverlay()

  for (const id of deleteSelection.value) {
    await mediaStore.deleteMedia(id) // Assume deleteMedia takes an id and deletes it
    selectedIds.value = selectedIds.value.filter(selectedId => selectedId !== id)
  }

  updateSelectionOrder()
  deleteSelection.value = []
  deleteMode.value = false
  await mediaStore.getMedia(props?.restaurant?._id, props?.userGroup?._id)
  await usersStore.hideLoadingOverlay()
}

const confirmSelection = () => {
  if(props.multiSelect)
    emit('media-selected', selectedIds.value)
  else
    emit('media-selected', selectedIds.value[0])

}

const previewImage = () => {
  // @todo should throw a indicator in the front-end that no image is not selected
  // Ensure there's a file selected (single file only)
  if (!selectedFile.value) {
    console.error('No file selected.')
    
    return
  }
  uploadImage({crop:props.crop})
}

// Must match MAX_UPLOAD_BYTES in lib/mediaUploadGuards.js (server-enforced).
const MAX_UPLOAD_MB = 20
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024

const uploadImage = async ({crop = false} = {}) => {
  if (!selectedFile.value) {
    console.error('No file selected.')

    return
  }
  if (selectedFile.value.size > MAX_UPLOAD_BYTES) {
    document.querySelector(".js-error").textContent = `Image too large. Please upload an image ${MAX_UPLOAD_MB}MB or smaller.`

    return
  }
  document.querySelector(".js-error").textContent = ''
  await usersStore.showLoadingOverlay()

  const formData = new FormData()
  formData.append('image', selectedFile.value)

  //if restaurant passed in then add the restaurantId to the form data so any upload gets associated
  if(props.restaurant){
    formData.append('restaurantId', props.restaurant._id)
  }
  if(props.userGroup){
    formData.append('userGroupId', props.userGroup._id)
  }

  try {
    const response = await fetch('/media/', {
      method: 'POST',
      body: formData,
    })

    if (!response.ok) {
      // Surface the server's error (413 oversized, 400 bad type) instead of
      // guessing — the server response includes the real reason.
      let serverError = null
      try {
        serverError = (await response.json())?.error
      } catch (e) { /* non-JSON error body */ }
      throw new Error(serverError || `Upload failed. Please try a different image (max ${MAX_UPLOAD_MB}MB).`)
    }

    // The upload response only returns after the server has finished the
    // Mongo metadata writes and S3 rendition uploads, so the listing is
    // immediately consistent — refetch right away. (A legacy 2s setTimeout
    // sat here as a workaround from when that wasn't true; it made every
    // upload feel slow and still missed slower uploads.)
    await mediaStore.getMedia(props?.restaurant?._id, props?.userGroup?._id)
    if(!props.multiSelect && selectedIds.value.length == 0){
      selectedIds.value = [media.value[0].imageId]
    } else if(props.multiSelect){
      selectMedia(media.value[0])
    }

    await usersStore.hideLoadingOverlay()

    if(crop){
      let responseBodyString = await response.text()
      mediaIdToCrop.value = responseBodyString
      showMediaCropper.value = true
    }

  } catch (error) {
    console.error('Upload error:', error)
    document.querySelector(".js-error").textContent = error
    await usersStore.hideLoadingOverlay()
  }
}

const handleMediaCropped = croppedMediaId => {
  mediaStore.getMedia(props?.restaurant?._id, props?.userGroup?._id)
  setTimeout(() => {
    //force refresh of img
    media.value = media.value.map(m => {
      if(m.imageId === croppedMediaId){
        //probably not a good idea to be manipulating the actual imageId
        //issue if we save the media and it changes the imageId
        m.imageId = croppedMediaId + "?t=" + new Date().getTime()
      }
      
      return m
    })
    if(!props.multiSelect) selectedIds.value = [media.value[0].imageId]
    updateSelectionOrder()
  }, 100)

}

onMounted(() => {
  mediaStore.getMedia(props?.restaurant?._id, props?.userGroup?._id).then(fetchedMedia => {
    // media.value = fetchedMedia;
    // console.log("media", media.value)
    updateSelectionOrder()
  })
})

//watch props.restaurant and fetch media
watch(() => props.restaurant, newVal => {
  if(newVal)
    mediaStore.getMedia(newVal._id, props?.userGroup?._id).then(fetchedMedia => {
      updateSelectionOrder()
    })
})

watch(() => props.userGroup, newVal => {
  if(newVal)
    mediaStore.getMedia(props?.restaurant?._id, newVal._id).then(fetchedMedia => {
      // media.value = fetchedMedia;
      // console.log("media", media.value)
      updateSelectionOrder()
    })
})

watch(() => props.preselectedMediaIds, newVal => {
  mediaStore.getMedia(props?.restaurant?._id, props?.userGroup?._id).then(fetchedMedia => {
    if(newVal)
      selectedIds.value = [...newVal]
    else
      selectedIds.value = []
    updateSelectionOrder()
  })
})

// Refetch media whenever the dialog re-opens. Without this, parents that keep
// the component mounted (e.g. RestaurantEditRestaurantDialog) only fetch in
// onMounted — so reopening the chooser after the underlying media collection
// changed showed stale thumbnails. See plan-08 review L1.
watch(() => props.isDialogVisible, opened => {
  if (!opened) return
  mediaStore.getMedia(props?.restaurant?._id, props?.userGroup?._id).then(() => {
    updateSelectionOrder()
  })
})

function updateSelectionOrder() {
  if (media.value) {
    //get rid of any selectedIds that are not in media
    selectedIds.value = selectedIds.value.filter(id => media.value.find(m => m.imageId === id))

    // Create an array the same length as media.value, initialized to -1
    selectionOrder.value = media.value.map(item => {
      const index = selectedIds.value.indexOf(item.imageId)
      
      return index > -1 ? index + 1 : -1  // Return 1-based index if found, otherwise -1
    })
  } else {
    selectionOrder.value = []  // Reset to empty if media is not loaded
  }
}
</script>

<template>
  <div>
    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmDialogOpen"
      confirmation-msg="Are you sure you want to delete?"
      confirm-btn-text="Yes"
      cancel-btn-text="No"
      @confirm="deleteConfirmed(); isConfirmDialogOpen = false;"
      @cancel=" isConfirmDialogOpen = false;"
    />
    <MediaCropper
      v-model:is-dialog-visible="showMediaCropper"
      :media-id-to-crop="mediaIdToCrop"
      title="Crop Image"
      :stencil="cropStencil"
      :aspect-ratio="cropAspectRatio"
      @media-cropped="handleMediaCropped"
    />
    <VDialog
      :model-value="props.isDialogVisible"
      persistent
      fullscreen
      :retain-focus="false"
    >
      <VToolbar
        dark
        color="primary"
        class="toolbar-header"
      >
        <VSpacer />
        <VToolbarTitle>{{ title || 'Upload/Select an Image' }}</VToolbarTitle>
        <VSpacer />
      </VToolbar>
      <VCard
        class="pa-0 main-content"
        @keyup="changesMade = true"
      >
        <VContainer class="pa-0">
          <span class="js-error" />
          <VContainer class="input-holder">
            <!-- Image Preview -->
            <VImg
              v-if="imagePreview"
              :src="imagePreview"
              aspect-ratio="1"
              class="mb-4"
              height="300"
            />

            <!-- File Input -->
            <VFileInput
              ref="inputRef"
              v-model="selectedFile"
              label="Upload an image"
              accept="image/*"
              @change="previewImage"
            />
          </VContainer>
          <VItemGroup>
            <VContainer>
              <div class="image-grid">
                <button
                  class="add-image-button ma-1"
                  @click="() => { inputRef.click() }"
                >
                  <VIcon
                    size="40"
                    color="grey"
                  >
                    mdi-plus
                  </VIcon>
                </button>
                <VCol
                  v-for="(item, i) in media"
                  :key="item.imageId"
                  class="pa-1"
                >
                  <VImg
                    class="image-grid-image"
                    :class="{
                      'delete-selected': deleteMode && deleteSelection.includes(item.imageId),
                      'selected': !deleteMode && selectedIds.includes(item.imageId),
                      'deletable': deleteMode && isDeletable(item)
                    }"
                    :src="mediaUrl(item.imageId, 'thumb')"
                    cover
                    @click="selectMedia(item)"
                  >
                    <div
                      v-if="deleteMode && isDeletable(item) && deleteSelection.includes(item.imageId)"
                      class="image-grid-image-check bg-error pa-1 pt-0"
                    >
                      <VIcon
                        color="#ffffff"
                        size="16"
                      >
                        mdi-delete
                      </VIcon>
                    </div>
                    <div
                      v-else-if="!deleteMode && selectedIds.includes(item.imageId)"
                      class="image-grid-image-check bg-primary pa-1 pt-0"
                    >
                      <span v-if="multiSelect">{{ selectionOrder[i] }}</span>
                      <VIcon
                        color="#ffffff"
                        size="16"
                      >
                        mdi-check
                      </VIcon>
                    </div>
                    <!-- Optionally show a lock icon or similar for non-deletable items -->
                    <div
                      v-if="deleteMode && !isDeletable(item)"
                      class="image-grid-image-lock"
                    >
                      <VIcon
                        color="#ff0000"
                        size="16"
                      >
                        mdi-lock
                      </VIcon>
                    </div>
                  </VImg>
                </VCol>
              </div>
            </VContainer>
          </VItemGroup>
        </VContainer>
        <div
          v-if="deleteMode"
          class="action-button-info"
        >
          Select items to delete
        </div>
        <div
          v-else
          class="text-primary text-center text-xs"
        >
          Max file size: 20MB
        </div>
        <div class="action-buttons gap-4">
          <VBtn
            v-if="media?.length > 0 && !deleteMode"
            @click="confirmSelection"
          >
            {{ selectedIds.length == 0 ? 'Okay' : 'Display Selected' }}
          </VBtn>
          <VBtn
            v-if="!deleteMode"
            color="secondary"
            variant="tonal"
            @click="requestClose"
          >
            Close
          </VBtn>
          <VBtn
            v-if="!restaurant && !userGroup"
            :color="(deleteMode ? '' : 'error')"
            @click="deleteMode = !deleteMode"
          >
            {{ deleteMode ? 'Cancel Delete' : 'Delete' }}
          </VBtn>
          <VBtn
            v-if="deleteMode && deleteSelection.length > 0"
            color="error"
            @click="isConfirmDialogOpen = true"
          >
            Confirm Delete
          </VBtn>
        </div>
      </VCard>
    </VDialog>
  </div>
</template>

<style lang="scss" scoped>
.deletable {
  border-color: red; /* Style for deletable images */
}
.image-grid-image-lock {
  position: absolute;
  right: 0.5rem;
  bottom: 0.5rem; /* Positioning for lock icon */
}

.v-toolbar-title{
  flex: unset;
  margin-inline-start:unset;
}

.toolbar-header{
  position: fixed;
  z-index: var(--ds-z-modal);
}

.main-content{
  top: 60px;
  position: relative;
  padding-bottom: 80px !important;
}

.input-holder {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr)) ;
  grid-template-rows: repeat(auto-fill, minmax(100px, 1fr)) ;
}

.image-grid-image {
  cursor: pointer;
  border-radius: var(--ds-radius-sm);
  border: var(--ds-space-1) solid transparent;
}

.image-grid-image.selected {
  border-color: rgb(var(--v-theme-primary));
}

.image-grid-image-check {

  position: absolute;
  right: 0rem;
  top: 0rem;
  padding: 0;
  border-bottom-left-radius: 0.3rem;
}


.image-grid-image-delete {

  position: absolute;
  left: 0rem;
  top: 0rem;
  padding: 0;
  border-bottom-right-radius: 0.3rem;
}

@media screen and (min-width: 670px) {

  .image-grid-image-delete {
    opacity: 0;
    cursor: pointer;
    transition: opacity 100ms ease-in-out;
  }
  .image-grid-image:hover .image-grid-image-delete {
    opacity: 1;
  }
}

.add-image-button {
  background: var(--ds-surface-inset);
  height: auto;
  border-radius: var(--ds-radius-sm);
  transition: filter var(--ds-duration-normal) ease-in-out;
}

@media screen and (min-width: 670px) {
  .add-image-button:hover {
    filter: brightness(0.9);
  }
}

.action-buttons {
  display: flex;
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  z-index: var(--ds-z-dropdown);
  background: var(--ds-surface-card);
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  border-top: thin solid var(--ds-border-default);
  padding: 1rem;
  box-shadow: 0px -3px 6px rgba(0,0,0,0.025);
}

.action-button-info{
  position: unset;
  text-align-last: center;
  width: 100%;
  z-index: var(--ds-z-dropdown);
  background: var(--ds-surface-card);
  border-top: thin solid var(--ds-border-default);
  padding: 0.5rem;
  box-shadow: 0px -3px 6px rgba(0,0,0,0.025);
  font-size: 0.8rem;
  color: #f00;
}

.cropper {
  height: 600px;
  width: 600px;
  background: #DDD;
}
</style>
